import { jurisdictions } from './de/jurisdictions';

const { federal: _federal, model: _model, ...states } = jurisdictions;

/** German texts of the module, below its ID: `legal.texts.title`. */
export const de = {
  name: 'Rechtstexte',
  description:
    'Gesetze, Verordnungen und technische Regeln zu Brandschutz, Bauordnung und Arbeitsschutz, paragraphengenau durchsuchbar.',
  nav: { texts: 'Rechtstexte', sources: 'Quellen verwalten' },
  permissions: {
    norm: {
      read: {
        label: 'Rechtstexte lesen',
        description:
          'Rechtstexte durchsuchen, Quellen und Normen öffnen. Andere Module verlinken damit die Rechtsgrundlage von Mängeln.',
      },
    },
    source: {
      manage: {
        label: 'Rechtsquellen verwalten',
        description:
          'Quellen anlegen, ändern und löschen, Volltexte einfügen, Dateien importieren und Standardinhalte übernehmen.',
      },
      fetch: {
        label: 'Rechtstexte aus amtlichen Quellen abrufen',
        description:
          'Zusammen mit „Rechtsquellen verwalten“: Quellen aus dem Katalog anlegen, „Neu abrufen“ und „Alle aktualisieren“, neue Fassungen übernehmen und eigene Adressen auf amtlichen Portalen eintragen.',
      },
    },
  },
  roles: { reader: 'Rechtstexte lesen', manager: 'Rechtstexte verwalten' },
  audit: {
    source: {
      create: 'Rechtsquelle angelegt',
      update: 'Rechtsquelle geändert',
      norms: 'Normen einer Rechtsquelle geändert',
      delete: 'Rechtsquelle gelöscht',
    },
    retrieval: {
      request: 'Rechtsquelle neu abgerufen',
      'request-all': 'Alle Rechtsquellen aktualisiert',
      accept: 'Neue Fassung einer Rechtsquelle übernommen',
      discard: 'Abruf einer Rechtsquelle verworfen',
      upload: 'Datei einer Rechtsquelle hochgeladen',
    },
  },
  settings: {
    state: {
      label: 'Bundesland',
      description:
        'Suche und Listen zeigen Bundesrecht, Musterregelungen und das Recht dieses Landes, bei „Alle Länder“ das Recht aller Länder.',
      options: { all: 'Alle Länder', ...states },
    },
  },
  jurisdictions,
  kinds: {
    law: 'Gesetz',
    regulation: 'Verordnung',
    'administrative-provision': 'Verwaltungsvorschrift',
    guideline: 'Richtlinie',
    'technical-rule': 'Technische Regel',
    dguv: 'DGUV-Regelwerk',
    standard: 'Norm (nur Verweis)',
  },
  topics: {
    'fire-safety': 'Brandschutz',
    'building-code': 'Bauordnung',
    'site-safety': 'Baustelle (SiGeKo)',
    'occupational-safety': 'Arbeitsschutz',
    'building-services': 'Anlagentechnik',
  },
  guides: {
    texts: {
      title: 'Rechtstexte nachschlagen',
      summary: 'Gesetze und Regeln durchsuchen, Normen lesen und die amtliche Fassung öffnen.',
    },
    sources: {
      title: 'Rechtsquellen pflegen',
      summary:
        'Quellen anlegen, Volltexte zerlegen, Dateien importieren und Standardinhalte übernehmen.',
    },
  },
  texts: {
    title: 'Rechtstexte',
    description:
      'Gesetze, Verordnungen und Regeln von Bund, Musterregelungen und Ländern, paragraphengenau durchsuchbar.',
    term: 'Suchbegriff',
    termPlaceholder: 'z. B. Brandschutztür selbstschließend oder § 35 BauO Bln',
    termHint: 'Ab zwei Zeichen. Wortformen zählen mit: „Treppenraum“ findet auch „Treppenräume“.',
    state: 'Bundesland',
    topic: 'Thema',
    allTopics: 'Alle Themen',
    results_one: '{{count}} Treffer',
    results_other: '{{count}} Treffer',
    cited: 'Fundstelle',
    noHits: 'Keine passende Stelle gefunden',
    noHitsHint: 'Andere Begriffe versuchen, das Thema zurücksetzen oder alle Länder wählen.',
    norms_one: '{{count}} Norm',
    norms_other: '{{count}} Normen',
    referenceOnly: 'Nur Verweis, Wortlaut nicht gespeichert',
    noText: 'Volltext noch nicht hinterlegt',
    empty: 'Noch keine Rechtstexte',
    emptyHint:
      'Hier stehen die Gesetze und Regeln der Firma, sobald die Verwaltung sie hinterlegt hat.',
    emptyManage:
      'Die Standardinhalte bringen acht Gesetze und Regeln zu Brandschutz und Arbeitsschutz mit, weitere kommen per Import.',
    noSources: 'Keine Quellen für diese Auswahl',
    noSourcesHint: 'Ein anderes Thema oder alle Länder wählen.',
  },
  norm: {
    title: 'Norm',
    intro: 'Titel und Vorbemerkung',
    previous: 'Vorherige',
    next: 'Nächste',
    edition: 'Stand: {{edition}}',
    official: 'Amtliche Fassung öffnen',
    newTab: '(öffnet in neuem Tab)',
    table: 'Tabelle',
    noOfficial: 'Für diese Quelle ist keine amtliche Fassung hinterlegt.',
    unofficial: 'Nicht amtliche Lesefassung. Maßgeblich ist die amtliche Veröffentlichung.',
    notFound: 'Diese Norm gibt es nicht mehr',
    notFoundHint: 'Die Quelle wurde neu zerlegt oder gelöscht.',
  },
  source: {
    details: 'Angaben',
    contents: 'Inhalt',
    inactive: 'Inaktiv',
    inactiveTitle: 'Diese Quelle ist inaktiv',
    inactiveHint: 'Die Suche und andere Module finden sie nicht, nur die Pflege sieht sie.',
    standardTitle: 'Nur Verweis',
    standardHint:
      'Den Wortlaut von Normen wie DIN, VDE oder VdS speichert die STAW App nicht, er ist urheberrechtlich geschützt.',
    noText: 'Noch kein Volltext hinterlegt',
    noTextHint:
      'Den Text der amtlichen Fassung kopieren und einfügen. Er wird in Paragraphen, Abschnitte und Anhänge zerlegt.',
    noTextReader: 'Die Verwaltung der Firma kann den Volltext einfügen.',
    notFound: 'Quelle nicht gefunden',
    notFoundHint: 'Sie wurde gelöscht oder ist nicht mehr aktiv.',
    changed: 'Geändert {{when}}',
    notSet: 'Keine Angabe',
    open: 'Öffnen',
    pasteText: 'Volltext einfügen',
    delete: 'Löschen',
    deleteTitle: 'Rechtsquelle löschen?',
    deleteDescription:
      '„{{title}}“ wird mit allen Normen gelöscht. Rechtsgrundlagen in anderen Modulen bleiben als Text stehen.',
    deleted: 'Rechtsquelle gelöscht.',
    fields: {
      code: 'Kurzcode',
      title: 'Titel',
      jurisdiction: 'Geltungsbereich',
      kind: 'Art',
      topics: 'Themen',
      summary: 'Kurzbeschreibung',
      edition: 'Stand',
      officialUrl: 'Amtliche Fassung',
      fetchUrl: 'Abruf von',
      active: 'Aktiv',
      norms: 'Normen',
      updated: 'Geändert',
    },
  },
  form: {
    createTitle: 'Neue Rechtsquelle',
    editTitle: 'Rechtsquelle bearbeiten',
    codeHint: 'Kurzform, mit der Rechtsgrundlagen zitieren, z. B. „BauO Bln“ oder „ASR A2.3“.',
    summaryHint: 'Worum es geht, in zwei, drei Sätzen. Hilft beim Auswählen in der Liste.',
    editionHint: 'z. B. „zuletzt geändert durch Gesetz vom 09.07.2026“',
    officialUrlHint: 'Adresse der amtlichen Veröffentlichung. Die Normansicht verlinkt dorthin.',
    fetchUrlHint:
      'HTTPS-Adresse des Gesetzes auf einem amtlichen Portal, z. B. die Seite bei gesetze-im-internet.de, ein Landesrechtsportal oder ein PDF der Bauministerkonferenz. Nach dem Speichern wird der Wortlaut abgerufen.',
    activeHint: 'Inaktive Quellen finden weder die Suche noch andere Module.',
    created: 'Rechtsquelle angelegt.',
    updated: 'Änderungen gespeichert.',
    reload: 'Neu laden',
    conflictHint:
      'Jemand hat die Quelle inzwischen geändert. Neu laden und die Änderung erneut eintragen.',
  },
  text: {
    title: 'Volltext einfügen',
    description:
      'Den Text der amtlichen Fassung kopieren und hier einfügen. Er ersetzt die bisherigen Normen der Quelle.',
    label: 'Volltext',
    preview_one: 'Ergibt {{count}} Norm: {{numbers}}',
    preview_other: 'Ergibt {{count}} Normen: {{numbers}}',
    previewEmpty:
      'Noch keine Norm erkannt. Paragraphen beginnen mit „§ 1“, Abschnitte technischer Regeln mit „1“ oder „1.1“.',
    tooLong: 'Der Text ist länger als 900.000 Zeichen. Bitte auf mehrere Quellen aufteilen.',
    conflictHint:
      'Jemand hat die Quelle inzwischen geändert. Neu laden und erneut speichern, der eingefügte Text bleibt stehen.',
    save: 'Zerlegen und speichern',
    saved:
      'Volltext gespeichert: {{created}} neu, {{updated}} geändert, {{removed}} entfernt, {{unchanged}} unverändert.',
  },
  sources: {
    title: 'Rechtsquellen',
    description: 'Gesetze, Verordnungen und Regeln der Firma pflegen.',
    new: 'Neue Quelle',
    search: 'Quellen durchsuchen',
    searchPlaceholder: 'Titel oder Kurzcode',
    status: 'Status',
    active: 'Aktiv',
    allJurisdictions: 'Alle Geltungsbereiche',
    inactive: 'Inaktiv',
    empty: 'Noch keine Rechtsquellen',
    emptyHint:
      'Die Standardinhalte übernehmen oder eine Datei importieren. Einzelne Quellen lassen sich auch von Hand anlegen.',
    noMatches: 'Keine Quelle gefunden',
    noMatchesHint: 'Suchbegriff oder Geltungsbereich ändern.',
    defaults: 'Standardinhalte übernehmen',
    defaultsDone_one: '{{count}} Rechtstext übernommen.',
    defaultsDone_other: '{{count}} Rechtstexte übernommen.',
    defaultsNone: 'Alle Standardinhalte sind schon da.',
    import: 'Importieren',
  },
  import: {
    title: 'Rechtsquellen importieren',
    description:
      'JSON-Datei mit Quellen und ihren Normen, z. B. aus BrandDoku. Das Format beschreibt die Anleitung „Rechtsquellen pflegen“.',
    fileHint:
      'Bekannte Quellen werden über Geltungsbereich und Kurzcode oder Titel wiedererkannt und nur dort geändert, wo die Datei abweicht. Ein zweiter Import derselben Datei ändert nichts.',
    chooseFile: 'Datei auswählen',
    summary: '„{{file}}“: {{sources}} mit zusammen {{norms}}.',
    sourceCount_one: '{{count}} Quelle',
    sourceCount_other: '{{count}} Quellen',
    normCount_one: '{{count}} Norm',
    normCount_other: '{{count}} Normen',
    run: 'Importieren',
    running: 'Teil {{done}} von {{total}} wird importiert …',
    done: 'Import abgeschlossen.',
    close: 'Schließen',
    sourcesResult: 'Quellen',
    normsResult: 'Normen',
    created: 'Neu',
    updated: 'Geändert',
    removed: 'Entfernt',
    unchanged: 'Unverändert',
    errors: {
      unreadable: 'Die Datei ist kein gültiges JSON.',
      format:
        'Die Datei hat nicht das Format des Imports: Erwartet wird ein Objekt mit der Liste „sources“.',
      empty: 'Die Datei enthält keine Quellen.',
      tooLarge:
        'Quelle {{index}} („{{title}}“) ist größer als 900 KB. Den Volltext bitte auf mehrere Quellen aufteilen.',
      issue: 'Quelle {{index}} („{{title}}“): {{reason}}',
      invalid: 'Ein Feld entspricht nicht dem Format.',
      failed: 'Import abgebrochen. {{reason}}',
      partial_one: 'Der erste Teil ist übernommen, ein neuer Import setzt fort.',
      partial_other: 'Die ersten {{count}} Teile sind übernommen, ein neuer Import setzt fort.',
    },
  },
  retrieval: {
    title: 'Abruf aus amtlicher Quelle',
    description: 'Der Wortlaut kommt vom Portal, nichts wird umformuliert.',
    column: 'Abruf',
    all: 'Alle aktualisieren',
    allQueued:
      '{{created}} Quellen aus dem Katalog angelegt, {{queued}} Abrufe eingereiht, {{skipped}} übersprungen. Die Abrufe laufen nacheinander im Hintergrund.',
    fetch: 'Neu abrufen',
    queued: 'Abruf eingereiht.',
    never: 'Nie abgerufen',
    address: 'Adresse',
    fetchedAt: 'Zuletzt abgerufen',
    state: 'Zustand',
    parserMissing: 'Parser fehlt',
    parserMissingHint:
      'Das Portal bietet keinen Gesamttext zum Abruf an (eine Seite je Paragraph, nur ein Link mit Sitzungskennung oder ein Scan). Den Volltext von dort kopieren und über „Volltext einfügen“ hinterlegen.',
    uploadOnly: 'Datei hochladen',
    uploadOnlyHint:
      'Das Landesportal gibt den Text nur im Browser heraus. Dort die Vorschrift öffnen, „Aktuelle Gesamtausgabe“ wählen, „XML-Ansicht des Dokuments“ als .zip herunterladen und die Datei hier mit „XML-Datei hochladen“ einlesen.',
    portal: 'Zum Landesportal',
    more: 'Weitere Aktionen zum Abruf',
    uploads: {
      xml: 'XML-Datei hochladen',
      pdf: 'PDF hochladen',
      text: 'Volltext als Textdatei hochladen',
    },
    uploaded: 'Datei hochgeladen, sie wird eingelesen.',
    openDocument: 'Dokument im Browser öffnen',
    failedTitle: 'Abruf fehlgeschlagen',
    changesTitle: 'Neue Fassung prüfen',
    created_one: '{{count}} neue Norm',
    created_other: '{{count}} neue Normen',
    updated_one: '{{count}} geänderte Norm',
    updated_other: '{{count}} geänderte Normen',
    removed_one: '{{count}} Norm entfällt',
    removed_other: '{{count}} Normen entfallen',
    unchanged_one: '{{count}} Norm unverändert.',
    unchanged_other: '{{count}} Normen unverändert.',
    unverifiedTitle: 'Nicht wörtlich gefunden',
    unverifiedHint:
      'Diese Normen stehen nicht Zeichen für Zeichen im Dokument und werden nicht übernommen: {{numbers}}.',
    compare: 'Vorher und nachher vergleichen',
    before: 'Bisher',
    after: 'Neu',
    new: 'Noch nicht vorhanden',
    accept: 'Übernehmen',
    discard: 'Verwerfen',
    accepted: 'Neue Fassung übernommen.',
    discarded: 'Abruf verworfen, die bisherigen Normen bleiben.',
    status: {
      queued: 'Wartet',
      running: 'Wird abgerufen',
      ready: 'Änderungen prüfen',
      unchanged: 'Aktuell',
      accepted: 'Übernommen',
      discarded: 'Verworfen',
      failed: 'Fehler',
    },
    errors: {
      'invalid-url': 'Die Adresse ist keine HTTPS-Adresse auf dem Standardport.',
      'host-not-allowed':
        'Das Portal steht nicht auf der Liste amtlicher Quellen, auch nicht nach einer Weiterleitung.',
      'private-address': 'Die Adresse führt in ein internes Netz. Abgelehnt.',
      'too-many-redirects': 'Das Portal leitet zu oft weiter.',
      status: 'Das Portal hat das Dokument nicht geliefert (Fehlerstatus). Adresse prüfen.',
      'content-type': 'Das Portal liefert kein XML, HTML oder PDF.',
      'too-large': 'Das Dokument ist größer als erlaubt.',
      timeout: 'Das Portal hat nicht rechtzeitig geantwortet. Später erneut abrufen.',
      network: 'Das Portal war nicht erreichbar. Später erneut abrufen.',
      'unsupported-format': 'Das Format des Dokuments wird nicht erkannt.',
      unreadable: 'Das Dokument ist beschädigt oder nicht lesbar.',
      'no-text':
        'Im Dokument steht kein lesbarer Rechtstext. Viele Portale zeigen den Text nur mit JavaScript.',
      'no-verified-norms': 'Keine Norm hat die Wortlautprüfung bestanden. Nichts übernommen.',
      'norm-too-long': 'Eine Norm ist zu lang. Vermutlich fehlen Überschriften wie „§ 12“.',
      'source-deleted': 'Die Quelle wurde inzwischen gelöscht.',
      blocked:
        'Das Portal hat den automatischen Abruf abgewiesen (Schutz vor Bots), das passiert dort unregelmäßig. Später erneut abrufen oder das Dokument im Browser herunterladen und über „⋮“ hochladen.',
      unexpected: 'Unerwarteter Fehler beim Abruf.',
    },
  },
  basis: {
    placeholder: 'z. B. § 35 Abs. 1 BauO Bln; ASR A2.3 Nr. 5',
    search: 'Norm suchen',
    searchLabel: 'Norm für die Rechtsgrundlage suchen',
    searchPlaceholder: 'z. B. Treppenraum Rauchabschluss oder § 35 BauO Bln',
    minLength: 'Mindestens zwei Zeichen eingeben.',
    offline:
      'Die Normsuche braucht eine Verbindung. Die Rechtsgrundlage lässt sich trotzdem als Text eintragen.',
    loading: 'Suche läuft …',
    empty: 'Keine Norm gefunden.',
    tooLong: 'Die Rechtsgrundlage ist zu lang. Erst eine Angabe entfernen.',
    open: '{{citation}}: Wortlaut öffnen',
  },
  errors: {
    code: 'Kurzcode: höchstens 60 Zeichen.',
    title: 'Titel fehlt oder ist länger als 300 Zeichen.',
    jurisdiction: 'Geltungsbereich wählen: Bund, Muster oder ein Land.',
    kind: 'Art wählen.',
    topics: 'Themen aus der Liste wählen.',
    summary: 'Kurzbeschreibung: höchstens 400 Zeichen.',
    edition: 'Stand: höchstens 300 Zeichen.',
    officialUrl: 'Vollständige Adresse mit https:// angeben, höchstens 1000 Zeichen.',
    active: 'Aktiv ist ja oder nein.',
    text: 'Der Volltext fehlt, ist zu lang oder enthält keine erkennbare Norm.',
    normTooLong:
      'Eine Norm ist länger als 400.000 Zeichen. Den Text prüfen: Vermutlich fehlen Überschriften wie „§ 12“.',
    standardText: 'Normen wie DIN und VDE sind nur Verweise, ihr Wortlaut wird nicht gespeichert.',
    duplicate:
      'Eine Quelle mit diesem Kurzcode oder Titel gibt es in diesem Geltungsbereich schon.',
    normNumber: 'Die Nummer einer Norm fehlt oder ist länger als 60 Zeichen.',
    normHeading: 'Die Überschrift einer Norm ist länger als 500 Zeichen.',
    normOutline: 'Die Gliederung einer Norm ist länger als 500 Zeichen.',
    normText: 'Der Wortlaut einer Norm fehlt oder ist länger als 400.000 Zeichen.',
    norms: 'Höchstens 5000 Normen je Quelle.',
    duplicateNumber: 'Eine Nummer kommt in dieser Quelle zweimal vor.',
    duplicateSource: 'Die Quelle steht zweimal in der Datei.',
    invalid: 'Angabe prüfen.',
    versionConflict: 'Die Quelle wurde inzwischen geändert.',
    notFound: 'Die Quelle gibt es nicht mehr.',
    forbidden: 'Dafür fehlt die Berechtigung „Rechtsquellen verwalten“.',
    fetchUrl: 'Vollständige HTTPS-Adresse angeben, höchstens 1000 Zeichen.',
    fetchHost:
      'Nur Adressen amtlicher Portale sind erlaubt (Liste in der Anleitung „Rechtsquellen pflegen“).',
    noFetchUrl: 'Für diese Quelle ist keine Adresse zum Abrufen hinterlegt.',
    noParser: 'Für das Portal dieser Quelle gibt es keinen Parser.',
    retrievalRunning: 'Diese Quelle wird gerade abgerufen.',
    retrievalNotReady: 'Dieser Abruf wartet nicht mehr auf eine Entscheidung.',
    uploadOnly:
      'Diese Quelle lässt sich nicht abrufen. Den XML-Export im Landesportal herunterladen und hochladen.',
    uploadMissing: 'Die hochgeladene Datei gehört nicht zu dieser Quelle oder fehlt.',
  },
};
